import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { LogOut } from 'lucide-react'

import { shellAssets } from '../../constants/assets'
import { logoutTemporaryAdmin } from '../../auth/adminAuth'
import { useConfirmDialog } from '../../context/ConfirmDialogContext'

type TopbarProps = {
  title: string
  subtitle: ReactNode
  user: { name: string; email: string; avatar: string }
  onOpenNav: () => void
  showUser?: boolean
}

export const Topbar = ({
  title,
  subtitle,
  user,
  onOpenNav,
  showUser = true,
}: TopbarProps) => {
  const navigate = useNavigate()
  const { confirm, showSuccess } = useConfirmDialog()

  const handleLogout = async () => {
    const confirmed = await confirm({
      title: 'Sign Out',
      message: 'Are you sure you want to sign out of the Salzburg Cab dashboard?',
      confirmText: 'Sign Out',
      cancelText: 'Cancel',
      variant: 'warning',
      icon: 'logout',
    })

    if (!confirmed) return

    await showSuccess({
      title: 'Signed Out Successfully',
      message: 'You have been securely signed out. Redirecting to login...',
      autoCloseMs: 1200,
    })

    logoutTemporaryAdmin()
    navigate('/admin-login', { replace: true })
  }

  return (
    <header className="flex w-full flex-col gap-[16px] rounded-[14px] bg-white px-[23px] py-[15px] lg:flex-row lg:items-start lg:justify-between lg:gap-[20px]">
      <div className="flex items-start gap-[7px]">
        <button
          type="button"
          aria-label="Open navigation"
          onClick={onOpenNav}
          className="flex shrink-0 flex-col items-start rounded-[4.49px] bg-white px-[12px] py-[7px] shadow-dash-toggle lg:hidden"
        >
          <img alt="" aria-hidden className="block h-[20.653px] w-[29.118px] max-w-none" src={shellAssets.sidebarToggle} />
        </button>

        <span className="flex size-[28px] shrink-0 items-center justify-center rounded-[5.091px] bg-dash-lime">
          <img
            alt=""
            aria-hidden
            className="block size-[14.424px] max-w-none -scale-x-100 object-cover"
            src={shellAssets.titleIcon}
          />
        </span>

        <div className="flex min-w-0 flex-col items-start lg:w-[419px]">
          <p className="font-host text-[20px] font-medium leading-normal text-black lg:h-[32px]">{title}</p>
          <div className="font-host text-[16px] font-normal leading-normal text-dash-muted">{subtitle}</div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-[14px]">

        {showUser ? (
          <div className="flex h-[56px] min-w-0 flex-1 items-center justify-between rounded-[26px] bg-white px-[8px] py-[5px] shadow-dash-profile">
            <div className="flex min-w-0 items-center gap-[10px]">
              <img
                alt=""
                aria-hidden
                className="block size-[42px] shrink-0 rounded-full object-cover"
                src={user.avatar}
              />

              <div className="flex min-w-0 flex-col items-start">
                <p className="truncate font-host text-[15px] font-medium leading-tight text-dash-slate">
                  {user.name}
                </p>
                <p className="truncate font-host text-[13px] font-normal leading-tight text-dash-muted">
                  {user.email}
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={handleLogout}
              title="Log out from dashboard"
              aria-label="Log out"
              className="flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-full text-gray-400 transition hover:bg-red-50 hover:text-red-500"
            >
              <LogOut className="h-[18px] w-[18px]" strokeWidth={1.9} />
            </button>
          </div>
        ) : null}
      </div>
    </header>
  )
}
